import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, CssBaseline, useMediaQuery, Theme } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import Header from '../Header';
import Sidebar from '../Sidebar';
import Footer from '../Footer/Footer';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 64;

const MainLayout: React.FC = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth0();
  
  // State for sidebar open/close (mobile) and collapsed/expanded (desktop)
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Always show sidebar for all users, role-based filtering happens in Sidebar component
  const showSidebar = isAuthenticated;

  // Close sidebar on mobile by default
  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    } else {
      setIsSidebarOpen(true);
    }
  }, [isMobile]);

  const handleSidebarToggle = () => {
    if (showSidebar) {
      setIsSidebarOpen(!isSidebarOpen);
    }
  };

  const handleCollapseToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Calculate the current sidebar width based on state
  const sidebarWidth = isSidebarCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      {/* Header */}
      <Header
        drawerWidth={DRAWER_WIDTH}
        onSidebarToggle={handleSidebarToggle}
        showSidebar={showSidebar}
      />
      
      {/* Sidebar - shown for all authenticated users */}
      {showSidebar && (
        <Sidebar
          drawerWidth={DRAWER_WIDTH}
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant={isMobile ? 'temporary' : 'persistent'}
          collapsed={isSidebarCollapsed}
          onToggleCollapse={handleCollapseToggle}
        />
      )}
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { 
            md: showSidebar 
              ? `calc(100% - ${isSidebarOpen ? sidebarWidth : 0}px)` 
              : '100%' 
          },
          transition: (theme) => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          display: 'flex',
          flexDirection: 'column',
          pb: '96px',
        }}
      >
        {/* Toolbar spacer */}
        <Box sx={{ height: 75 }} />
        
        {/* Page content */}
        <Outlet />
      </Box>
      <Footer />
    </Box>
  );
};

export default MainLayout;